import { Alert, CheckboxField, SelectField, TextField } from '@rooming/ui';
import { useState } from 'react';
import { backoffice, type Room } from '../api';
import { ReasonDialog, useCommand } from '../common';

// Check-in emergency (ADR-0013, step 3): retire a room — and, if wanted, create its replacement — in one step. Whoever
// slept there is left without a bed; the stay keeps its confirmation. Super administrator and hotel administrator.

const CAUSES = [
  { value: 'AVERIA', label: 'Avería' },
  { value: 'BLOQUEO', label: 'Bloqueo del hotel' },
  { value: 'CAPACIDAD', label: 'Cambio de capacidad' },
  { value: 'OTRO', label: 'Otro motivo' },
] as const;
type Cause = (typeof CAUSES)[number]['value'];

const SEXES = [
  { value: 'FEMALE', label: 'Mujeres' },
  { value: 'MALE', label: 'Varones' },
  { value: 'ANY', label: 'Sin restricción' },
] as const;

export function RoomReplacementDialog({
  stayId,
  room,
  onClose,
}: {
  readonly stayId: string;
  readonly room: Room;
  readonly onClose: () => void;
}) {
  const { busy, run } = useCommand();
  const [cause, setCause] = useState<Cause>('AVERIA');
  const [withReplacement, setWithReplacement] = useState(true);
  const [label, setLabel] = useState(`${room.label} (reemplazo)`.slice(0, 40));
  const [capacity, setCapacity] = useState(String(room.capacity));
  const [sex, setSex] = useState<Room['sexRestriction']>(room.sexRestriction);
  const beds = Number(capacity);
  // A companions' room is always for women or for men.
  const sexOptions = SEXES.filter((option) => room.audience === 'PASSENGER' || option.value !== 'ANY');
  const replacementValid =
    !withReplacement || (label.trim() !== '' && Number.isInteger(beds) && beds >= 1 && beds <= 20);

  return (
    <ReasonDialog
      open
      title={`Retirar la habitación ${room.label}`}
      confirmLabel={withReplacement ? 'Retirar y reemplazar' : 'Retirar habitación'}
      danger
      busy={busy}
      disabled={!replacementValid}
      reasonLabel="Motivo (queda en la auditoría)"
      onClose={onClose}
      onConfirm={(reason) => {
        void run(
          () =>
            backoffice.replaceRoom(stayId, {
              roomId: room.id,
              cause,
              reason,
              ...(withReplacement ? { replacement: { label: label.trim(), capacity: beds, sexRestriction: sex } } : {}),
            }),
          'Habitación retirada. Quienes la ocupaban quedaron sin cama: ubicalos en Distribución.',
          [['rooms', stayId], ['board', stayId], ['distribution', stayId], ['stays']],
        ).then((ok) => {
          if (ok) onClose();
        });
      }}
    >
      <div className="flex flex-col gap-4">
        <Alert tone="warning" title="Quienes duermen ahí quedan sin cama">
          La habitación sale de servicio al instante y no se puede volver a usar. La estadía sigue confirmada para el
          resto del grupo, y nadie se reubica solo: después ubicás a cada persona en la distribución.
        </Alert>
        <SelectField
          label="¿Por qué se retira?"
          options={CAUSES.map((c) => ({ value: c.value, label: c.label }))}
          value={cause}
          onChange={(event) => {
            setCause(event.target.value as Cause);
          }}
        />
        <CheckboxField
          checked={withReplacement}
          onChange={(event) => {
            setWithReplacement(event.target.checked);
          }}
        >
          Crear una habitación de reemplazo
        </CheckboxField>
        {withReplacement && (
          <div className="grid gap-4 sm:grid-cols-3">
            <TextField
              label="Nombre de la nueva"
              value={label}
              maxLength={40}
              onChange={(event) => {
                setLabel(event.target.value);
              }}
            />
            <TextField
              label="Plazas"
              type="number"
              min={1}
              max={20}
              value={capacity}
              onChange={(event) => {
                setCapacity(event.target.value);
              }}
            />
            <SelectField
              label="Para"
              options={sexOptions.map((option) => ({ value: option.value, label: option.label }))}
              value={sex}
              onChange={(event) => {
                setSex(event.target.value as Room['sexRestriction']);
              }}
            />
          </div>
        )}
      </div>
    </ReasonDialog>
  );
}
